import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** How both agents read a file they are given. */
const FILE_TOOL_LABEL_KEYS = {
  survey_workbook: 'taxonomy.agent.tools.surveyWorkbook',
  read_range: 'taxonomy.agent.tools.readRange',
  read_formulas: 'taxonomy.agent.tools.readFormulas',
  sample_column: 'taxonomy.agent.tools.sampleColumn',
} as const;

/** The schema tools both agents carry, keyed to the label the chat shows for each step. */
const SHARED_SCHEMA_TOOL_LABEL_KEYS = {
  read_schema: 'taxonomy.agent.tools.readSchema',
  edit_schema_draft: 'taxonomy.agent.tools.editSchemaDraft',
  edit_list_values: 'taxonomy.agent.tools.editListValues',
  publish_schema: 'taxonomy.agent.tools.publishSchema',
} as const;

/** The schema agent's tools, keyed to the label the chat shows for each step. */
export const SCHEMA_TOOL_LABEL_KEYS = {
  ...SHARED_SCHEMA_TOOL_LABEL_KEYS,
  ...FILE_TOOL_LABEL_KEYS,
  find_library_lists: 'taxonomy.agent.tools.findLibraryLists',
  create_list: 'taxonomy.agent.tools.createList',
  check_names: 'taxonomy.agent.tools.checkNames',
  create_taxonomy: 'taxonomy.agent.tools.createTaxonomy',
} as const;

/** The taxonomy agent's tools, keyed to the label the chat shows for each step. */
export const TAXONOMY_TOOL_LABEL_KEYS = {
  read_taxonomy: 'taxonomy.agent.tools.readTaxonomy',
  update_records: 'taxonomy.agent.tools.updateRecords',
  add_records: 'taxonomy.agent.tools.addRecords',
  delete_records: 'taxonomy.agent.tools.deleteRecords',
  generate_tracking_codes: 'taxonomy.agent.tools.generateTrackingCodes',
  update_taxonomy: 'taxonomy.agent.tools.updateTaxonomy',
  ...SHARED_SCHEMA_TOOL_LABEL_KEYS,
  move_to_schema_version: 'taxonomy.agent.tools.moveToSchemaVersion',
  ...FILE_TOOL_LABEL_KEYS,
  import_rows: 'taxonomy.agent.tools.importRows',
} as const;

type ToolLabelKey =
  | (typeof TAXONOMY_TOOL_LABEL_KEYS)[keyof typeof TAXONOMY_TOOL_LABEL_KEYS]
  | (typeof SCHEMA_TOOL_LABEL_KEYS)[keyof typeof SCHEMA_TOOL_LABEL_KEYS];

/** Each tool's label in the current language, by the name the agent calls it by. */
export function useToolLabels(
  keys: Readonly<Record<string, ToolLabelKey>>,
): Record<string, string> {
  const { t } = useTranslation('knowledge');
  return useMemo(
    () => Object.fromEntries(Object.entries(keys).map(([tool, key]) => [tool, t(key)])),
    [t, keys],
  );
}
